import { PillButton } from "@/components/Button/PillButton";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { DialogBox } from "@/components/DialogBox/DialogBox";
import { navigateToAiUsagePolicy } from "@/components/TermsAndPolicyDialog/utils";
import { useAcceptOrgConsentV1Mutation } from "@/store/clientApi";
import { ensureError, localizeError } from "@/utils/helpers";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Link, Typography } from "@mui/material";
import { useState } from "react";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";

interface OrgConsentDialogProps {
  open: boolean;
  isOwner: boolean;
  onAccepted: () => void;
  onClose?: () => void;
}

const NonOwnerOrgConsentDialog = ({ open, onClose }: { open: boolean; onClose?: () => void }) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="org-consent-dialog-title"
      sx={{ ".MuiPaper-root": { width: "100%", maxWidth: "800px", margin: "16px" } }}
    >
      <DialogTitle id="org-consent-dialog-title">{t("common:title:orgConsent")}</DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Typography>{t("common:message.contactOrgOwner")}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("common:message.contactOrgOwnerRefresh")}
          </Typography>
        </Box>
      </DialogContent>
      {onClose && (
        <>
          <Divider />
          <DialogActions sx={{ padding: "1em" }}>
            <PillButton onClick={onClose}>{t("common:button.close")}</PillButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

const OwnerOrgConsentDialog = ({ open, onAccepted, onClose }: { open: boolean; onAccepted: () => void; onClose?: () => void }) => {
  const { t } = useTranslation();
  const [acceptOrgConsent] = useAcceptOrgConsentV1Mutation();
  const [acceptedAiPolicy, setAcceptedAiPolicy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      await acceptOrgConsent({ documentType: "ai-training" }).unwrap();
      onAccepted();
    } catch (err) {
      const error = ensureError(err);
      toast.error(localizeError(t, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogBox
      title={t("common:title:orgConsent")}
      sx={{ ".MuiPaper-root": { width: "100%", maxWidth: "800px", margin: "16px" } }}
      message={
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Typography>{t("common:message.reviewAndAgreeToOrgPolicies")}</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
            <Checkbox value={acceptedAiPolicy} onChange={(_, checked) => setAcceptedAiPolicy(checked)} />
            <Typography>
              <Trans
                components={{
                  a: <Link component={"button"} onClick={navigateToAiUsagePolicy} />
                }}
                t={t}
                i18nKey={"message.acceptAiUsagePolicy"}
              />
            </Typography>
          </Box>
        </Box>
      }
      onClose={(approve) => {
        if (approve && acceptedAiPolicy) {
          handleAccept();
        } else {
          onClose?.();
        }
      }}
      open={open}
      setOpen={() => {
        return;
      }}
      args={undefined}
      hideDecline={!onClose}
      hideClose={!onClose}
      declineText={t("common:button.cancel")}
      confirmText={t("common:button.accept")}
      confirmDisable={isSubmitting || !acceptedAiPolicy}
    />
  );
};

export const OrgConsentDialog = ({ open, isOwner, onAccepted, onClose }: OrgConsentDialogProps) => {
  if (!isOwner) {
    return <NonOwnerOrgConsentDialog open={open} onClose={onClose} />;
  }

  return <OwnerOrgConsentDialog open={open} onAccepted={onAccepted} onClose={onClose} />;
};
